/**
 * Calculation Engine for Dynamic Forms
 * Supports basic math operations, field references, and custom formulas
 */

// Supported operators and functions
const OPERATORS = {
  '+': (a, b) => a + b,
  '-': (a, b) => a - b,
  '*': (a, b) => a * b,
  '/': (a, b) => b !== 0 ? a / b : 0,
  '^': (a, b) => Math.pow(a, b),
  '%': (a, b) => a % b,
};

const FUNCTIONS = {
  'SUM': (...args) => args.reduce((sum, val) => sum + (val || 0), 0),
  'AVERAGE': (...args) => {
    const validArgs = args.filter(val => val !== null && val !== undefined);
    return validArgs.length > 0 ? validArgs.reduce((sum, val) => sum + val, 0) / validArgs.length : 0;
  },
  'MAX': (...args) => Math.max(...args.filter(val => val !== null && val !== undefined)),
  'MIN': (...args) => Math.min(...args.filter(val => val !== null && val !== undefined)),
  'ROUND': (num, decimals = 0) => Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals),
  'CEIL': (num) => Math.ceil(num),
  'FLOOR': (num) => Math.floor(num),
  'ABS': (num) => Math.abs(num),
  'SQRT': (num) => Math.sqrt(Math.max(0, num)),
  'POW': (base, exp) => Math.pow(base, exp),
  'LOG': (num) => Math.log(Math.max(0, num)),
  'IF': (condition, trueValue, falseValue) => condition ? trueValue : falseValue,
};

/**
 * Parse a formula string and extract field references
 * @param {string} formula - The formula string
 * @returns {Array} Array of field names referenced in the formula
 */
export const extractFieldReferences = (formula) => {
  if (!formula) return [];
  
  const fieldPattern = /\{([^}]+)\}/g;
  const matches = [];
  let match;
  
  while ((match = fieldPattern.exec(formula)) !== null) {
    matches.push(match[1]);
  }
  
  return [...new Set(matches)]; // Remove duplicates
};

/**
 * Replace field references in formula with actual values
 * @param {string} formula - The formula string
 * @param {Object} fieldValues - Object with field names as keys and values as values
 * @returns {string} Formula with field references replaced
 */
export const replaceFieldReferences = (formula, fieldValues) => {
  if (!formula) return '';
  
  return formula.replace(/\{([^}]+)\}/g, (match, fieldName) => {
    const value = fieldValues[fieldName];
    if (value === null || value === undefined || value === '') {
      return '0';
    }
    
    // Convert to number if possible
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '0' : numValue.toString();
  });
};

/**
 * Evaluate a mathematical expression safely
 * @param {string} expression - The mathematical expression
 * @returns {number} The result of the calculation
 */
export const evaluateExpression = (expression) => {
  try {
    // Replace function calls with their implementations
    let processedExpression = expression;
    
    // Handle function calls
    Object.keys(FUNCTIONS).forEach(funcName => {
      const funcRegex = new RegExp(`\\b${funcName}\\s*\\(`, 'gi');
      processedExpression = processedExpression.replace(funcRegex, `FUNCTIONS.${funcName}(`);
    });
    
    // Replace operators with function calls
    Object.keys(OPERATORS).forEach(op => {
      const opRegex = new RegExp(`\\${op}`, 'g');
      processedExpression = processedExpression.replace(opRegex, `OPERATORS['${op}']`);
    });
    
    // Create a safe evaluation context
    const context = {
      OPERATORS,
      FUNCTIONS,
      Math,
    };
    
    // Use Function constructor for safer evaluation than eval
    const func = new Function(...Object.keys(context), `return ${processedExpression}`);
    const result = func(...Object.values(context));
    
    return isNaN(result) || !isFinite(result) ? 0 : result;
  } catch (error) {
    console.error('Error evaluating expression:', error);
    return 0;
  }
};

/**
 * Calculate the result of a formula
 * @param {string} formula - The formula string
 * @param {Object} fieldValues - Object with field names as keys and values as values
 * @param {number} decimalPlaces - Number of decimal places for the result
 * @param {boolean} currency - Whether to format as currency
 * @returns {Object} Calculation result with value, formattedValue, and error
 */
export const calculateFormula = (formula, fieldValues, decimalPlaces = 2, currency = false) => {
  try {
    if (!formula || !formula.trim()) {
      return {
        value: 0,
        formattedValue: formatNumber(0, decimalPlaces, currency),
        error: null,
      };
    }
    
    // Replace field references with values
    const expression = replaceFieldReferences(formula, fieldValues);
    
    // Evaluate the expression
    const result = evaluateExpression(expression);
    
    // Round to specified decimal places
    const roundedResult = Math.round(result * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
    
    return {
      value: roundedResult,
      formattedValue: formatNumber(roundedResult, decimalPlaces, currency),
      error: null,
    };
  } catch (error) {
    return {
      value: 0,
      formattedValue: formatNumber(0, decimalPlaces, currency),
      error: error.message,
    };
  }
};

/**
 * Format a number with specified decimal places and currency formatting
 * @param {number} value - The number to format
 * @param {number} decimalPlaces - Number of decimal places
 * @param {boolean} currency - Whether to format as currency
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimalPlaces = 2, currency = false) => {
  if (currency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  }
  
  return value.toFixed(decimalPlaces);
};

/**
 * Validate a formula syntax
 * @param {string} formula - The formula to validate
 * @returns {Object} Validation result with isValid and error message
 */
export const validateFormula = (formula) => {
  try {
    if (!formula || !formula.trim()) {
      return { isValid: true, error: null };
    }
    
    // Extract field references to check syntax
    const fieldReferences = extractFieldReferences(formula);
    
    // Basic syntax validation - check for balanced parentheses
    let parenCount = 0;
    for (let char of formula) {
      if (char === '(') parenCount++;
      if (char === ')') parenCount--;
      if (parenCount < 0) {
        return { isValid: false, error: 'Unmatched closing parenthesis' };
      }
    }
    
    if (parenCount !== 0) {
      return { isValid: false, error: 'Unmatched opening parenthesis' };
    }
    
    // Check for valid field reference syntax
    const fieldPattern = /\{[^}]*\}/g;
    const matches = formula.match(fieldPattern) || [];
    
    for (let match of matches) {
      const fieldName = match.slice(1, -1);
      if (!fieldName.trim()) {
        return { isValid: false, error: 'Empty field reference {}' };
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(fieldName)) {
        return { isValid: false, error: `Invalid field name: ${fieldName}` };
      }
    }
    
    return { isValid: true, error: null };
  } catch (error) {
    return { isValid: false, error: error.message };
  }
};

/**
 * Get available functions and their descriptions for help text
 * @returns {Object} Object with function names as keys and descriptions as values
 */
export const getAvailableFunctions = () => {
  return {
    'SUM': 'Sum of all arguments: SUM(field1, field2, ...)',
    'AVERAGE': 'Average of all arguments: AVERAGE(field1, field2, ...)',
    'MAX': 'Maximum value: MAX(field1, field2, ...)',
    'MIN': 'Minimum value: MIN(field1, field2, ...)',
    'ROUND': 'Round to decimal places: ROUND(value, decimals)',
    'CEIL': 'Round up: CEIL(value)',
    'FLOOR': 'Round down: FLOOR(value)',
    'ABS': 'Absolute value: ABS(value)',
    'SQRT': 'Square root: SQRT(value)',
    'POW': 'Power: POW(base, exponent)',
    'LOG': 'Natural logarithm: LOG(value)',
    'IF': 'Conditional: IF(condition, trueValue, falseValue)',
  };
};

/**
 * Get available operators for help text
 * @returns {Array} Array of operator strings
 */
export const getAvailableOperators = () => {
  return ['+', '-', '*', '/', '^', '%'];
};
